import React from "react";
import { useHistory, useLocation } from "react-router-dom";
import { styled } from "@mui/system";
import SettingsIcon from "@mui/icons-material/Settings";
import MenuIcon from "@mui/icons-material/Menu";
import MenuOpenIcon from "@mui/icons-material/MenuOpen";
import NavigateNextIcon from "@mui/icons-material/NavigateNext";
import Notifications from "../../../components/Notifications/Notifications";
import LoggedInMenu from "../../../components/LoggedInMenu/LoggedInMenu";
import FONTS_FAMILY from "../../../theme/FontsFamily";
import LocalStorageService from "../../../services/LocalStorageService";

const HeaderContainer = styled("header")({
    height: "64px",
    backgroundColor: "#192873",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    padding: "0 32px",
    boxSizing: "border-box",
    color: "#FFFFFF",
    width: "100%",
    boxShadow: "0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)",
    position: "sticky",
    top: 0,
    zIndex: 50,
});

const LeftSection = styled("div")({
    display: "flex",
    alignItems: "center",
    gap: "16px",
});

const BreadcrumbNav = styled("div")({
    display: "flex",
    alignItems: "center",
    gap: "6px",
    fontFamily: "Inter, sans-serif",
    fontSize: "14px",
    fontWeight: "500",
    color: "rgba(255, 255, 255, 0.7)",
});

const BreadcrumbItem = styled("span")(({ isCurrent }) => ({
    display: "inline-flex",
    alignItems: "center",
    gap: "4px",
    color: isCurrent ? "#FFFFFF" : "rgba(255, 255, 255, 0.75)",
    fontWeight: isCurrent ? "700" : "500",
    cursor: isCurrent ? "default" : "pointer",
    transition: "all 0.15s ease",
    whiteSpace: "nowrap",
    textOverflow: "ellipsis",
    overflow: "hidden",
    maxWidth: "320px",
    "&:hover": {
        color: "#FFFFFF",
        textDecoration: isCurrent ? "none" : "underline",
    },
}));

const RightSection = styled("div")({
    display: "flex",
    alignItems: "center",
    gap: "16px",
});

const IconButtonWrapper = styled("button")({
    cursor: "pointer",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    color: "#FFFFFF",
    backgroundColor: "transparent",
    border: "none",
    padding: "8px",
    borderRadius: "9999px",
    transition: "all 0.2s ease",
    "&:hover": {
        backgroundColor: "rgba(255, 255, 255, 0.1)",
    },
    "&:active": {
        transform: "scale(0.95)",
    },
});

function DashboardHeader({ onToggleSidebar, sidebarOpen }) {
    const history = useHistory();
    const location = useLocation();
    const [currentLang, setCurrentLang] = React.useState(LocalStorageService.getItem('language') || 'es');

    React.useEffect(() => {
        const handleLangChange = (e) => {
            if (e?.detail) {
                setCurrentLang(e.detail);
            } else {
                setCurrentLang(LocalStorageService.getItem('language') || 'es');
            }
        };
        window.addEventListener('languageChange', handleLangChange);
        return () => window.removeEventListener('languageChange', handleLangChange);
    }, []);

    const isEn = currentLang === 'en';

    const getBreadcrumbs = () => {
        const pathname = location.pathname;

        // Si el usuario está en el inicio del escritorio, no se muestra el breadcrumb
        if (pathname === "/app/mi-escritorio" || pathname === "/app" || pathname === "/app/") {
            return [];
        }

        const crumbs = [{ label: isEn ? "Home" : "Inicio", path: "/app/mi-escritorio" }];

        if (pathname.includes("/app/mensajes")) {
            crumbs.push({ label: isEn ? "Messages" : "Mensajes" });
        } else if (pathname.includes("/app/documentos")) {
            crumbs.push({ label: isEn ? "Documents" : "Documentos" });
        } else if (pathname.includes("/app/historial")) {
            crumbs.push({ label: isEn ? "History" : "Historial" });
        } else if (pathname.includes("/app/empresas")) {
            crumbs.push({ label: isEn ? "Companies" : "Empresas" });
        } else if (pathname.includes("/app/mi-cuenta")) {
            if (pathname.includes("configuracion")) {
                crumbs.push({ label: isEn ? "Settings" : "Configuración" });
            } else {
                crumbs.push({ label: isEn ? "My Account" : "Mi Cuenta" });
            }
        } else if (pathname.includes("/app/serviceRequestedDetails")) {
            crumbs.push({ label: isEn ? "Application Details" : "Detalles de la solicitud" });
        } else if (pathname.includes("/solicitar-tramite")) {
            crumbs.push({ label: isEn ? "New Application" : "Solicitud de Trámite" });
        } else if (pathname.includes("/todos-los-tramites")) {
            crumbs.push({ label: isEn ? "Services Catalog" : "Catálogo de Trámites" });
        } else if (pathname.includes("/preguntas-frecuentes")) {
            crumbs.push({ label: isEn ? "FAQ" : "Preguntas Frecuentes" });
        } else if (pathname.includes("/base-legal")) {
            crumbs.push({ label: isEn ? "Legal Framework" : "Base Legal" });
        } else if (pathname.includes("/contacto")) {
            crumbs.push({ label: isEn ? "Contact" : "Contacto" });
        }

        return crumbs;
    };

    const breadcrumbs = getBreadcrumbs();

    return (
        <HeaderContainer>
            <LeftSection>
                <IconButtonWrapper
                    onClick={onToggleSidebar}
                    style={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center"
                    }}
                >
                    {sidebarOpen ? <MenuOpenIcon /> : <MenuIcon />}
                </IconButtonWrapper>

                <BreadcrumbNav>
                    {breadcrumbs.map((crumb, idx) => {
                        const isLast = idx === breadcrumbs.length - 1;
                        return (
                            <React.Fragment key={idx}>
                                {idx > 0 && (
                                    <NavigateNextIcon sx={{ fontSize: "18px", color: "rgba(255, 255, 255, 0.5)" }} />
                                )}
                                <BreadcrumbItem
                                    isCurrent={isLast}
                                    onClick={() => {
                                        if (!isLast && crumb.path) {
                                            history.push(crumb.path);
                                        }
                                    }}
                                >
                                    {crumb.label}
                                </BreadcrumbItem>
                            </React.Fragment>
                        );
                    })}
                </BreadcrumbNav>
            </LeftSection>

            <RightSection>
                <IconButtonWrapper>
                    <Notifications color="white" />
                </IconButtonWrapper>
                <LoggedInMenu />
            </RightSection>
        </HeaderContainer>
    );
}

export default DashboardHeader;
